'use client';

import * as Popover from '@radix-ui/react-popover';
import { useEventListener } from '@woonstad/helpers-hooks';
import clsx from 'clsx';
import { useState } from 'react';

import { BasicDuurzaamheid, BasicFietsenberging, BasicInternet, BasicLift, BasicParkeren, BasicVeiligheid, BasicWasmachine } from '../../icons/basic';
import { Typography } from '../typography/Typography';

export const knownFacilities = ['internet', 'tuin', 'parkeer', 'fiets', 'lift', 'was', 'safety'];

export type FacilityEntry = {
  type: string;
  title: string;
  description?: string;
}

const IconWrapper = ({ facility }: { facility: FacilityEntry }) => {
  const Icon = getFacilityIcon(facility.type);

  return (
    <div
      className={clsx(
        'flex size-10 items-center justify-center bg-icon-button-enabled text-icon-button-enabled',
        (facility.title && facility.description) && 'hover:bg-icon-button-hover hover:text-icon-button-hover',
      )}
    >
      <Icon className="size-6" />
    </div>
  );
};

type Props = {
  facility: FacilityEntry;
}

export const FacilityIcon = ({ facility }: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // On mobile devices, close the popover when the user taps outside of it - also closes when start scrolling
  useEventListener('pointerdown', (e) => {
    setIsOpen(false);
  });

  if (!facility.title) {
    return <IconWrapper facility={facility} />;
  }

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <Popover.Trigger
        onMouseLeave={() => setIsOpen(false)}
        onMouseEnter={() => setIsOpen(true)}
        onClick={(e) => {
          e.preventDefault();
          setIsOpen(true);
        }}
      >
        <span className="sr-only">{facility.title}</span>
        <IconWrapper facility={facility} />
      </Popover.Trigger>

      <Popover.Content
        className="z-10 max-w-sm bg-surface-default p-6 shadow-large"
        hideWhenDetached
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={() => setIsOpen(false)}
      >
        <div className="space-y-4">
          <Typography
            variant="label-large"
            textColorClassname="text-tooltip-title"
            type="h3"
          >
            <span className="capitalize">
              {facility.title}
            </span>
          </Typography>

          {facility.description && (
            <Typography
              variant="content-body"
              textColorClassname="text-tooltip-text"
            >
              {facility.description}
            </Typography>
          )}
        </div>

        <Popover.Arrow className="h-4 w-8 fill-default" />
      </Popover.Content>
    </Popover.Root >
  );
};

function getFacilityIcon(facility: string) {
  // eslint-disable-next-line react/display-name
  return (props: Omit<React.SVGProps<SVGSVGElement>, 'ref'> & React.RefAttributes<SVGSVGElement>) => {
    switch (facility) {
      case 'fiets':
        return <BasicFietsenberging {...props} />;

      case 'internet':
        return <BasicInternet {...props} />;

      case 'lift':
        return <BasicLift {...props} />;

      case 'parkeer':
        return <BasicParkeren {...props} />;

      case 'safety':
        return <BasicVeiligheid {...props} />;

      case 'was':
        return <BasicWasmachine {...props} />;

      case 'tuin':
        return <BasicDuurzaamheid {...props} />;

      default:
        return null;
    }
  };
}
